import ServiceLayout from '@/pages/services/components/ServiceLayout';
import type { ServiceContent } from '@/pages/services/components/ServiceLayout';

export const content: ServiceContent = {
  tag: 'AI Marketing',
  title: '集客を増やしても、事業が伸びるとは限らない。',
  subtitle: '顧客の課題と、届ける価値を明確にします。',
  description:
    '施策や発信を増やす前に、顧客が抱える課題と、自社が届ける価値を明確にします。LUVIRAはAIを活用し、顧客理解、発信内容、施策実行、反応確認をつなげます。集客活動を、事業の次の判断につなげます。',
  icon: 'ri-bar-chart-box-line',
  heroColor: {
    blob1: 'linear-gradient(135deg, #C026D3, #7C3AED)',
    blob2: 'linear-gradient(135deg, #D946EF, #6366F1)',
    accent: '#E879F9',
    glow: 'from-fuchsia-400 to-violet-400',
  },
  aboutTitle: 'AI集客支援とは',
  aboutParagraphs: [
    '企業の事業内容や顧客、既存の集客活動を確認し、AIを活用して集客の企画・発信・検証を支援するサービスです。',
    'LUVIRAは、AIを使った集客活動を事業上の判断とつなげます。',
  ],
  challenges: [
    '発信や広告を続けているが、問い合わせにつながらない。',
    '集客施策が増え、何が成果につながっているか分からない。',
    '自社の強みや選ばれる理由を言語化できていない。',
    '誰に向けて発信すべきか決められない。',
    'AIを使って発信を効率化したいが、使い方が分からない。',
    '集客の反応を確認し、次の施策に活かせていない。',
  ],
  featuresTitle: 'AI集客支援でできること',
  features: [
    {
      icon: 'ri-user-search-line',
      title: '顧客を知る',
      items: ['顧客が抱える課題と自社の接点を整理します。'],
    },
    {
      icon: 'ri-megaphone-line',
      title: '伝える内容を定める',
      items: ['自社の強みと選ばれる理由を言語化します。'],
    },
    {
      icon: 'ri-play-circle-line',
      title: '施策を動かす',
      items: ['集客施策の優先順位を検討し、実行します。'],
    },
    {
      icon: 'ri-robot-line',
      title: 'AIで発信を支援する',
      items: ['AIを活用した発信の効率化を支援します。'],
    },
    {
      icon: 'ri-line-chart-line',
      title: '反応を次の判断につなげる',
      items: ['施策の反応を確認し、次の改善につなげます。'],
    },
  ],
  valueTitle: '集客改善によって得られる価値',
  value: [
    '誰に向けて発信するのかを明確にしやすくなる。',
    '自社の強みや選ばれる理由を伝えやすくなる。',
    '集客施策の優先順位を決めやすくなる。',
    '施策の反応をもとに、次の判断をしやすくなる。',
    '集客活動を継続的に見直しやすくなる。',
  ],
  whyTitle: 'なぜLuviraなのか',
  why: [
    'LUVIRAは、発信や広告を増やすことだけを目的にしません。事業内容と顧客の状況を確認します。自社の強みと顧客の課題を整理します。集客施策の優先順位を検討します。実施した施策の反応を確認します。必要に応じて、次の改善内容を検討します。',
  ],
  casesTitle: '支援事例',
  casesDescription: '現在、掲載準備中です。',
  flowTitle: '支援までの流れ',
  flow: [
    { step: 'STEP 1', title: '事業と顧客を確認する', items: ['事業内容と顧客の状況を把握します。'] },
    { step: 'STEP 2', title: '集客上の課題を整理する', items: ['集客の滞りと原因を整理します。'] },
    { step: 'STEP 3', title: '伝える内容と施策を決める', items: ['伝える内容と施策の優先順位を定めます。'] },
    { step: 'STEP 4', title: '発信と集客を実行する', items: ['施策を実行し、反応を集めます。'] },
    { step: 'STEP 5', title: '反応を確認して改善する', items: ['反応を確認し、次の施策へつなげます。'] },
  ],
  faq: [
    {
      q: 'AIに詳しくなくても相談できますか？',
      a: 'はい。AIの知識は必要ありません。事業内容、顧客、現在の集客状況を確認し、AIを活用できる部分を一緒に考えます。',
    },
    {
      q: 'どのような集客施策が対象になりますか？',
      a: 'Webサイト、SNS、記事、広告、問い合わせ導線など、事業内容と顧客の状況に応じて対象となる施策を検討します。',
    },
    {
      q: '発信する内容が決まっていなくても相談できますか？',
      a: 'はい。自社の強み、顧客の課題、選ばれる理由を確認し、伝える内容を一緒に考えます。',
    },
    {
      q: 'AIで発信内容をすべて作成してもらえますか？',
      a: 'AIを活用した発信内容の作成を支援します。ただし、事業上の判断や最終確認が必要な部分は、企業と一緒に検討します。',
    },
    {
      q: '現在行っている集客施策も見てもらえますか？',
      a: '現在の施策、発信内容、問い合わせまでの流れを確認し、改善できる部分を検討します。',
    },
    {
      q: '集客の成果を保証してもらえますか？',
      a: '成果を一律に保証するものではありません。顧客、施策、反応を確認し、次の判断と改善につなげる進め方を検討します。',
    },
    {
      q: '継続的な支援が必要ですか？',
      a: '課題や集客状況によって異なります。必要な支援範囲と頻度を確認したうえで、進め方を検討します。',
    },
    {
      q: 'AIシステムの開発もできますか？',
      a: '現在の業務や集客環境を確認したうえで、システム開発が必要かどうかを検討します。',
    },
  ],
  cta: {
    title: '集客の停滞を、次の判断に変える。',
    description:
      '発信や広告を続けているものの、問い合わせにつながらない。AIを使って集客を効率化したいものの、何から始めるべきか分からない。その状態からご相談いただけます。LUVIRAが事業内容と顧客の状況を確認し、集客の課題と次に取り組む施策を一緒に考えます。',
    buttonText: '集客の停滞を相談する',
    serviceKey: 'AI集客支援',
  },
};

export default function AiMarketingPage() {
  return <ServiceLayout content={content} />;
}